<template>
  <div>
    <!-- Only render modalOnly QuickSearch directly -->
    <QuickSearch
      v-if="mode === 'modalOnly'"
      ref="quickSearch"
      class-name="search-form u-text-center"
      :search-page-url="searchPageUrl"
      :placeholder="$t('searchPlaceholder')"
      @clickSearch="setIsExpanded(false)"
      @clickSuggestion="setIsExpanded(false)"
    />

    <!-- Otherwise render full UI with button + modal -->
    <template v-else>
      <!-- Button: SR-toggle -->
      <div id="app-quicksearch-sr">
        <button
          role="button"
          class="sr-only sr-only--selectable"
          aria-controls="app-quicksearch-container"
          :aria-expanded="isExpanded"
          @click="clickOpen($event)"
        >
          {{ $t("goToSearchSr") }}
        </button>
      </div>

      <!-- Button: Toggle -->
      <div class="page-actions__search">
        <button
          role="button"
          class="page-actions__search btn--search"
          :aria-label="$t('searchButton')"
          :title="$t('searchButton')"
          @click="clickOpen($event)"
        >
          <Icon :type="IconTypes.Search" :size="IconSizes.SmallPlus" />
          <span class="page-actions__action-label u-hide-for-medium-down">Søk</span>
        </button>
      </div>

      <!-- Modal: Quick search -->
      <div
        id="app-quicksearch-container"
        ref="searchContainer"
        class="quicksearch-container"
        @keydown.esc="clickClose($event)"
      >
        <Transition name="quicksearch-slide">
          <div v-show="isExpanded" class="u-bg-neutral quicksearch__content">
            <!-- Button: Close -->
            <button
              type="button"
              class="btn btn--small quicksearch__close"
              :aria-label="$t('close')"
              @click="clickClose"
            >
              <Icon :type="IconTypes.Close" :size="IconSizes.Small" />
            </button>

            <!-- Quick search -->
            <QuickSearch
              ref="quickSearch"
              class-name="search-form u-text-center"
              :search-page-url="searchPageUrl"
              @clickSearch="setIsExpanded(false)"
              @clickSuggestion="setIsExpanded(false)"
              @keydownEsc="clickClose"
            />
          </div>
        </Transition>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref } from "vue";
import Icon from "../../components/icon/icon.vue";
import { IconSizes, IconTypes } from "../../components/icon/icon.model";
import QuickSearch from "../../components/quickSearch/quickSearch.vue";

withDefaults(defineProps<{
  searchPageUrl: string;
  mode?: 'full' | 'modalOnly'; // default = 'full'
}>(), {
  mode: 'full',
});
const isExpanded = ref<boolean>();

const quickSearch = ref<{ input: HTMLInputElement | null } | null>(null);
const searchContainer = ref(null);

onMounted(() => {
  document.addEventListener("click", (e: Event) => {
    if (
      searchContainer.value &&
      (searchContainer.value as HTMLElement).contains(e.target as Node) ===
        false
    ) {
      setIsExpanded(false);
    }
  });
});

/**
 * Click open
 */
const clickOpen = (e: Event) => {
  e.preventDefault();
  e.stopPropagation();

  isExpanded.value = true;

  nextTick(() => {
    quickSearch.value?.input?.focus();
  });
};
/**
 * Click close
 */
function clickClose(e: Event) {
  e.preventDefault();

  isExpanded.value = false;

  quickSearch.value?.input?.blur();
}

/**
 * Set is expanded
 */
function setIsExpanded(expandedState: boolean) {
  isExpanded.value = expandedState;
}
</script>
